import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonGroup, { ButtonGroupProps } from '@cloudscape-design/components/button-group';
import PromptInput from '@cloudscape-design/components/prompt-input';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Badge from '@cloudscape-design/components/badge';
import LiveRegion from '@cloudscape-design/components/live-region';
import ChatBubble from '@cloudscape-design/chat-components/chat-bubble';
import Avatar from '@cloudscape-design/chat-components/avatar';
import { getChatHistory, postChatMessage, ChatRecord, MappingChange, TransformChange } from '../services/api';

export interface ChatPanelProps {
  jobId: string;
  processedBy?: string;
  onMappingChange?: () => void;
  onReassignment?: (targetAccountType: string) => void;
  pendingPrompt?: string | null;
  onPromptConsumed?: () => void;
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function MappingChangeIndicator({ changes }: { changes: MappingChange[] }) {
  if (changes.length === 0) return null;
  return (
    <Box margin={{ top: 'xs' }}>
      <SpaceBetween size="xxs">
        <Box variant="small" color="text-status-info">
          {changes.length} mapping{changes.length !== 1 ? 's' : ''} updated:
        </Box>
        {changes.map((change, i) => (
          <Box key={i} variant="small" padding={{ left: 's' }}>
            <SpaceBetween direction="horizontal" size="xxs">
              <Badge color="blue">{change.sourceField}</Badge>
              <span>→</span>
              <Badge color="green">{change.targetField}</Badge>
              <Box variant="small" color="text-body-secondary">
                ({change.confidenceScore}%)
              </Box>
            </SpaceBetween>
            {change.reason && (
              <Box variant="small" color="text-body-secondary" padding={{ left: 's' }}>
                {change.reason}
              </Box>
            )}
          </Box>
        ))}
      </SpaceBetween>
    </Box>
  );
}

function TransformChangeIndicator({ changes }: { changes?: TransformChange[] }) {
  if (!changes || changes.length === 0) return null;
  return (
    <Box margin={{ top: 'xs' }}>
      <SpaceBetween size="xxs">
        <Box variant="small" color="text-status-info">
          {changes.length} transform{changes.length !== 1 ? 's' : ''} applied:
        </Box>
        {changes.map((change, i) => (
          <Box key={i} variant="small" padding={{ left: 's' }}>
            <SpaceBetween direction="horizontal" size="xxs">
              <Badge color="blue">{change.sourceField}</Badge>
              <span>→</span>
              <Badge color="grey">{change.transformRule?.type || 'transform'}</Badge>
            </SpaceBetween>
            {change.reason && (
              <Box variant="small" color="text-body-secondary" padding={{ left: 's' }}>
                {change.reason}
              </Box>
            )}
          </Box>
        ))}
      </SpaceBetween>
    </Box>
  );
}

/** Inline actions for incoming (assistant) chat bubbles. */
function AssistantActions({ text }: { text: string }) {
  const [feedbackGiven, setFeedbackGiven] = useState<'helpful' | 'not-helpful' | null>(null);

  const items: ButtonGroupProps.ItemOrGroup[] = [
    {
      type: 'group',
      text: 'Feedback',
      items: [
        {
          type: 'icon-button',
          id: 'helpful',
          iconName: feedbackGiven === 'helpful' ? 'thumbs-up-filled' : 'thumbs-up',
          text: 'Helpful',
          disabled: feedbackGiven !== null,
          disabledReason:
            feedbackGiven === 'helpful'
              ? '\u201cHelpful\u201d feedback has been submitted.'
              : '\u201cHelpful\u201d option is unavailable after \u201cnot helpful\u201d feedback submitted.',
        },
        {
          type: 'icon-button',
          id: 'not-helpful',
          iconName: feedbackGiven === 'not-helpful' ? 'thumbs-down-filled' : 'thumbs-down',
          text: 'Not helpful',
          disabled: feedbackGiven !== null,
          disabledReason:
            feedbackGiven === 'not-helpful'
              ? '\u201cNot helpful\u201d feedback has been submitted.'
              : '\u201cNot helpful\u201d option is unavailable after \u201chelpful\u201d feedback submitted.',
        },
      ],
    },
    {
      type: 'icon-button',
      id: 'copy',
      iconName: 'copy',
      text: 'Copy',
      popoverFeedback: <StatusIndicator type="success">Message copied</StatusIndicator>,
    },
  ];

  const handleClick = useCallback(
    ({ detail }: { detail: ButtonGroupProps.ItemClickDetails }) => {
      if (detail.id === 'helpful') setFeedbackGiven('helpful');
      else if (detail.id === 'not-helpful') setFeedbackGiven('not-helpful');
      else if (detail.id === 'copy') {
        navigator.clipboard.writeText(text).catch(() => {});
      }
    },
    [text],
  );

  return (
    <ButtonGroup
      ariaLabel="Chat bubble actions"
      variant="icon"
      onItemClick={handleClick}
      items={items}
    />
  );
}

/** Render markdown content from agent responses. */
function MarkdownContent({ text }: { text: string }) {
  return (
    <div className="chat-markdown">
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}

/** Outgoing (user) chat bubble. */
function UserBubble({
  record,
  hideAvatar,
}: {
  record: ChatRecord;
  hideAvatar: boolean;
}) {
  const timeStr = formatTime(record.timestamp);
  return (
    <ChatBubble
      ariaLabel={`You at ${timeStr}`}
      type="outgoing"
      hideAvatar={hideAvatar}
      avatar={<Avatar ariaLabel="You" tooltipText="You" />}
    >
      {record.message}
    </ChatBubble>
  );
}

/** Incoming (assistant) chat bubble with markdown, actions, and mapping changes. */
function AssistantBubble({
  record,
  hideAvatar,
}: {
  record: ChatRecord;
  hideAvatar: boolean;
}) {
  const timeStr = formatTime(record.timestamp);
  return (
    <ChatBubble
      ariaLabel={`Generative AI assistant at ${timeStr}`}
      type="incoming"
      hideAvatar={hideAvatar}
      actions={<AssistantActions text={record.message} />}
      avatar={
        <Avatar
          color="gen-ai"
          iconName="gen-ai"
          ariaLabel="Generative AI assistant"
          tooltipText="Generative AI assistant"
        />
      }
    >
      <MarkdownContent text={record.message} />
      <MappingChangeIndicator changes={record.mappingChanges} />
      <TransformChangeIndicator changes={record.transformChanges} />
    </ChatBubble>
  );
}

/** Loading state bubble while agent is generating. */
function LoadingBubble() {
  return (
    <ChatBubble
      ariaLabel="Generative AI assistant is generating a response"
      type="incoming"
      avatar={
        <Avatar
          loading={true}
          color="gen-ai"
          iconName="gen-ai"
          ariaLabel="Generative AI assistant"
          tooltipText="Generative AI assistant"
        />
      }
    >
      <Box color="text-status-inactive">Generating response</Box>
    </ChatBubble>
  );
}

function ChatHistory({
  records,
  sending,
}: {
  records: ChatRecord[];
  sending: boolean;
}) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [records.length, sending]);

  if (records.length === 0 && !sending) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding={{ top: 'xl', bottom: 'xl' }}>
        <SpaceBetween size="xs">
          <Box variant="p" fontWeight="bold">No messages yet</Box>
          <Box variant="small">Ask the agent to refine mappings, explain decisions, or re-run the analysis.</Box>
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <div
      role="region"
      aria-label="Chat messages"
      style={{ overflowY: 'auto', padding: '8px 0', flex: 1 }}
    >
      <SpaceBetween size="m">
        {records.map((record, i) => {
          const prev = i > 0 ? records[i - 1] : null;
          const hideAvatar = prev !== null && prev.role === record.role;

          return record.role === 'user' ? (
            <UserBubble key={i} record={record} hideAvatar={hideAvatar} />
          ) : (
            <AssistantBubble key={i} record={record} hideAvatar={hideAvatar} />
          );
        })}
        {sending && <LoadingBubble />}
      </SpaceBetween>
      <div ref={endRef} />
    </div>
  );
}

const ChatPanel: React.FC<ChatPanelProps> = ({ jobId, processedBy, onMappingChange, onReassignment, pendingPrompt, onPromptConsumed }) => {
  const [chatHistory, setChatHistory] = useState<ChatRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [latestMessage, setLatestMessage] = useState('');

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getChatHistory(jobId);
      setChatHistory(response.chatHistory);
    } catch (e: any) {
      setError(e.message || 'Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (processedBy === 'claudeagent') {
      loadHistory();
    }
  }, [jobId, processedBy]);

  // Pick up pending prompts from the transform column
  useEffect(() => {
    if (pendingPrompt) {
      setInputValue(pendingPrompt);
      onPromptConsumed?.();
    }
  }, [pendingPrompt, onPromptConsumed]);

  const handleSend = async () => {
    const message = inputValue.trim();
    if (!message) return;

    setInputValue('');
    setSending(true);
    setError(null);

    const userRecord: ChatRecord = {
      timestamp: new Date().toISOString(),
      role: 'user',
      message,
      mappingChanges: [],
    };
    setChatHistory(prev => [...prev, userRecord]);

    try {
      const response = await postChatMessage(jobId, message);
      const assistantRecord: ChatRecord = {
        timestamp: new Date().toISOString(),
        role: 'assistant',
        message: response.message,
        mappingChanges: response.mappingChanges,
        transformChanges: response.transformChanges,
      };
      setChatHistory(prev => [...prev, assistantRecord]);
      setLatestMessage(response.message);

      // If the agent made mapping, transform, or reassignment changes, notify the parent to refresh
      if (response.mappingChanges.length > 0 || (response.transformChanges && response.transformChanges.length > 0) || response.updatedGroupingSummary) {
        onMappingChange?.();
      }
      // If this was a reassignment, notify parent to show remapping indicator
      if (response.updatedGroupingSummary) {
        // Extract target account type from the groups that were added/modified
        const groups = response.updatedGroupingSummary.groups ?? [];
        // The message typically says "Moved rows ... to <type>"
        const moveMatch = message.match(/(?:move|set|change|reclassify)\s+rows?\s+[\d,\s]+\s+(?:to|as)\s+(\w+)/i);
        if (moveMatch) {
          onReassignment?.(moveMatch[1].trim());
        } else if (groups.length > 0) {
          // Fallback: use the last group (most likely the target)
          onReassignment?.(groups[groups.length - 1].accountType ?? '');
        }
      }
    } catch (e: any) {
      setError(e.message || 'Failed to send message');
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  if (processedBy !== 'claudeagent') return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', padding: '16px 20px 0', overflow: 'hidden' }}>
      <div style={{ paddingBottom: 12, borderBottom: '1px solid #e9ebed', flexShrink: 0 }}>
        <Header
          variant="h2"
          actions={
            <Button iconName="refresh" onClick={loadHistory} loading={loading} variant="icon" ariaLabel="Refresh chat" />
          }
          description="Ask the agent to refine mappings, explain decisions, or re-run analysis."
        >
          Agent Chat
        </Header>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: 12, minHeight: 0 }}>
        {error && (
          <Box margin={{ bottom: 'xs' }}>
            <StatusIndicator type="error">{error}</StatusIndicator>
          </Box>
        )}

        {loading ? (
          <Box padding={{ top: 'l' }} textAlign="center">
            <StatusIndicator type="loading">Loading chat history...</StatusIndicator>
          </Box>
        ) : (
          <ChatHistory records={chatHistory} sending={sending} />
        )}
      </div>

      <LiveRegion hidden={true}>{latestMessage}</LiveRegion>

      <div style={{ padding: '12px 0 16px', borderTop: '1px solid #e9ebed', flexShrink: 0 }}>
        <PromptInput
          onChange={({ detail }) => setInputValue(detail.value)}
          onAction={handleSend}
          value={inputValue}
          actionButtonAriaLabel="Send message"
          actionButtonIconName="send"
          ariaLabel="Chat prompt input"
          autoFocus
          maxRows={8}
          minRows={3}
          placeholder="Ask a question about this job..."
          disabled={sending || loading}
          disableActionButton={sending || !inputValue.trim()}
        />
      </div>
    </div>
  );
};

export default ChatPanel;
